const faqItems = [
  ['What is a CAN bus decoder?', 'A CAN bus decoder maps raw CAN frames into meaningful signals using IDs, payload bytes, bit positions, scaling, offsets, units, and context.'],
  ['What is a CAN ID?', 'A CAN ID identifies a message on the bus. Its meaning depends on the ECU network, vehicle, embedded system, and bus configuration.'],
  ['What is the payload?', 'The payload is the data byte field in a CAN frame. One payload can contain one or more packed signals.'],
  ['What are scaling and offset?', 'Scaling and offset convert raw bit values into engineering values such as RPM, speed, temperature, or pressure.'],
  ['What is a DBC file?', 'A DBC file describes CAN messages and signals, including ID, bit position, length, byte order, scaling, offset, and units.'],
  ['Why is CAN decoding difficult?', 'Signals are often manufacturer-specific and require evidence from repeatable tests or verified documentation.'],
  ['Can SmartCAN decode unknown signals automatically?', 'SmartCAN can assist with organization and candidate hints, but exact decoding requires validated formulas and signal definitions.'],
  ['Does SmartCAN support OBD data analysis?', 'SmartCAN includes generic OBD-II decoding for supported response frames and keeps raw frames visible for deeper analysis.']
];

export function CanBusDecoderGuide() {
  return (
    <main className="seo-page">
      <section className="section page-shell seo-hero">
        <div>
          <p className="eyebrow">CAN Bus Decoder guide</p>
          <h1>CAN Bus Decoder Guide</h1>
          <p className="lead">
            CAN decoding turns raw CAN IDs and payload bytes into useful signal names, values, and units. This guide
            explains the core concepts behind CAN Bus Decoder workflows.
          </p>
          <div className="hero-actions">
            <a className="button primary" href="/analyzer">Analyze a CAN log</a>
            <a className="button secondary" href="/decode">Browse signal hints</a>
            <a className="button ghost" href="/what-is-smartcan">What is SmartCAN?</a>
          </div>
        </div>
      </section>

      <section className="section seo-content-grid">
        <article className="data-panel">
          <p className="eyebrow">Raw frame</p>
          <h2>CAN IDs and payload bytes</h2>
          <p>
            A CAN frame contains an ID, DLC, and payload bytes. The ID tells listeners which message was sent, while
            the payload carries compact binary data from an ECU or embedded controller.
          </p>
        </article>
        <article className="data-panel">
          <p className="eyebrow">Signals</p>
          <h2>Bits, scaling, and offsets</h2>
          <p>
            A signal definition tells a decoder which bits to read, how long the value is, whether byte order matters,
            and how to convert the raw number using factor and offset.
          </p>
        </article>
        <article className="data-panel">
          <p className="eyebrow">DBC files</p>
          <h2>Reusable definitions</h2>
          <p>
            DBC-style files store message and signal definitions so a CAN decoder can apply known formulas repeatedly.
            Without those definitions, careful evidence and testing are needed.
          </p>
        </article>
      </section>

      <section className="band">
        <div className="section-heading compact">
          <p className="eyebrow">Example</p>
          <h2>Raw CAN frame to decoded signal</h2>
          <p>This simplified example shows the idea. Actual definitions must match the vehicle, ECU, bus, and signal source.</p>
        </div>
        <div className="table-wrap">
          <table className="can-table live comparison-table">
            <thead><tr><th>Raw field</th><th>Example</th><th>Interpretation</th></tr></thead>
            <tbody>
              <tr><td>CAN ID</td><td>0x7E8</td><td>Generic OBD-II response frame</td></tr>
              <tr><td>Payload</td><td>04 41 0C 1A F8</td><td>Mode 01 PID 0C response bytes</td></tr>
              <tr><td>Signal</td><td>Engine RPM</td><td>((0x1A * 256) + 0xF8) / 4 = 1726 RPM</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="section two-column">
        <article className="status-panel">
          <p className="eyebrow">SmartCAN workflow</p>
          <h2>Organize CAN data before decoding</h2>
          <p>
            SmartCAN helps inspect IDs, filter payload bytes, export data, decode supported OBD responses, and surface
            verified community signal hints when matching CAN IDs are observed.
          </p>
          <a className="button primary" href="/live">Open live demo</a>
        </article>
        <article className="code-panel">
          <p className="eyebrow">Why context matters</p>
          <h2>Same bytes, different meaning</h2>
          <p>
            The same CAN ID or byte pattern can mean different things across platforms. Treat hints as candidates until
            a signal definition is verified with evidence.
          </p>
          <a className="button secondary" href="/best-can-bus-tools">Compare CAN tools</a>
        </article>
      </section>

      <section className="section">
        <div className="section-heading compact">
          <p className="eyebrow">FAQ</p>
          <h2>CAN Bus Decoder questions</h2>
        </div>
        <div className="faq-grid">
          {faqItems.map(([question, answer]) => (
            <article className="data-panel faq-card" key={question}>
              <h3>{question}</h3>
              <p>{answer}</p>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}
